import { useEffect, useState } from 'react'

import { useServerTimeOffsetMs } from '~/hooks/useServerTimeOffsetMs'
import { now as serverNow } from '~/models/clock'
import { secondsToSpotDeadline } from '~/pages/SpotBidding/models/marketPhase'

/**
 * `isCountingDown` の間だけ 1 秒 tick で入札締切までの残り秒数を返す。 `undefined` は
 * カウントダウン対象外 (= 呼び出し元は既定表示に倒す)。
 *
 * page-header の市場フェーズバッジ (`SpotPhaseBadge`) と承認申請確認 modal
 * (AC-BSP-012、 `SpotBiddingPage`) の両方が「表示している間だけ締切までの残り時間を
 * 秒単位で更新する」 という同じ要件を持つため、 1 module に集約する (元は
 * `SpotPhaseBadge` のローカル hook だった)。 ページ全体の現在時刻
 * (`useNowByMinute`) は 1 分粒度なので秒を出せない。 かといってページ側を
 * 1 秒 tick にすると入札一覧まで毎秒再描画されるため、 秒粒度の時計はカウントダウンを
 * 表示する箇所だけが個別に持つ (時間前入札の `GcCountdown` と同じ切り分け)。
 */
export const useSpotDeadlineCountdown = (
  targetDate: Date,
  isCountingDown: boolean,
): number | undefined => {
  // server time offset は session RPC 完了時 (= 初回描画より後) に確定しうる。 購読して
  // 依存に含めないと、 偽装時刻・端末時計ずれ環境で確定前に拾ったブラウザ時計由来の
  // 残時間が次の tick まで残る ([[useNowByMinute]] が同じ理由で購読している)。
  const offsetMs = useServerTimeOffsetMs()
  const [now, setNow] = useState(() => serverNow())
  // 「カウントダウン対象に入った瞬間」 と 「offset 確定」 は effect を待たずに描画中で
  // now を取り直す (React の「描画中に state を調整する」 pattern)。 effect まで待つと、
  // 対象外で開いたままの表示がカウントダウン対象に変わった瞬間 / session RPC で
  // サーバー時刻が確定した瞬間に、 古い now 由来の残時間が 1 フレーム見える。
  const resetKey = `${String(isCountingDown)}:${offsetMs}`
  const [prevResetKey, setPrevResetKey] = useState(resetKey)
  if (prevResetKey !== resetKey) {
    setPrevResetKey(resetKey)
    setNow(serverNow())
  }

  // 1 秒 tick。 「対象に入った瞬間 / offset 確定時に now を取り直す」 のは上の
  // 描画中 reset が既に済ませているので、 ここで再度 `setNow` はしない (同じ発火条件で
  // 追加の再描画が 1 回増えるだけになる)。
  useEffect(() => {
    if (!isCountingDown) return undefined
    const tick = setInterval(() => {
      setNow(serverNow())
    }, 1000)
    return () => {
      clearInterval(tick)
    }
  }, [isCountingDown, offsetMs])

  return isCountingDown ? secondsToSpotDeadline(now, targetDate) : undefined
}
